import React, { useState, useMemo, useEffect } from "react";
import { Modal } from "../../components/ui/Modal";
import { useReelCreator } from "./hooks/useReelCreator";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { PostSelectorMediaCard } from "@/components/MediaCard";
import { useSimpleMediaLibrary } from "@/core/MediaLibrary/hooks/useSimpleMediaLibrary";
import { Post } from "@/core/types/postTypes";
import { MediaStage, PostStatus } from "@/core/types/mediaTypes";
import { VideoFrameExtractor } from "./VideoFrameExtractor";
import { UnifiedAuthenticatedImage } from "@/components/ui/UnifiedAuthenticatedImage";
import { mediaCacheUtils } from "@/core/MediaLibrary/hooks/useMediaQuery";
import { useVideoPlayer } from "@/hooks/useVideoPlayer";

interface ReelCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | null;
  editingPost?: Post | null;
  onSuccess?: (reelId: string, response: any) => void;
  onError?: (error: string) => void;
}

export const ReelCreatorModal: React.FC<ReelCreatorModalProps> = ({
  isOpen,
  onClose,
  clientId,
  editingPost,
  onSuccess,
  onError,
}) => {
  const [currentStep, setCurrentStep] = useState<"video" | "thumbnail">(
    editingPost ? "thumbnail" : "video",
  );
  const [showUsedMedia, setShowUsedMedia] = useState(false);
  const [extractedFrames, setExtractedFrames] = useState<MediaItem[]>([]);

  const videoPlayer = useVideoPlayer({ autoPlay: false });
  const videoRef = videoPlayer.videoRef;

  const reelCreator = useReelCreator(clientId, {
    editingPost,
    onSuccess: (reelId, response) => {
      console.log(`Reel ${editingPost ? "updated" : "created"} successfully:`, {
        reelId,
        response,
      });
      onSuccess?.(reelId, response);
      handleClose();
    },
    onError: (error) => {
      console.error(
        `Failed to ${editingPost ? "update" : "create"} reel:`,
        error,
      );
      onError?.(error);
    },
  });

  // Use stage filter (approved only) and optionally exclude items in posts
  const stageFilter = useMemo(() => [MediaStage.APPROVED], []);
  const excludePostStatuses = useMemo(() => {
    // If showing used media, don't exclude any post statuses (show all media)
    if (showUsedMedia) return undefined;
    // Otherwise exclude media that are part of ANY post (draft, ready, scheduled, published, etc.)
    return Object.values(PostStatus);
  }, [showUsedMedia]);

  // Use the unified media library hook
  const mediaLibrary = useSimpleMediaLibrary({
    clientId: clientId || "",
    autoLoad: true,
    contentTypes: ["photos", "reels"], // Load photos for thumbnails and reels for videos
    stageFilter,
    excludePostStatuses,
  });

  // Clear extracted frames when modal opens
  // Note: media cache is already invalidated by PostCreatorModalContainer
  useEffect(() => {
    if (isOpen) {
      setExtractedFrames([]);
    }
  }, [isOpen]);

  // Initialize video player when a video is selected
  useEffect(() => {
    if (reelCreator.selectedVideo) {
      videoPlayer.initialize(reelCreator.selectedVideo);
    } else {
      videoPlayer.reset();
    }
  }, [reelCreator.selectedVideo, videoPlayer.initialize, videoPlayer.reset]);

  // Merge extracted frames with library images (extracted frames first)
  const { images: libraryImages, shortVideos, longVideos } = mediaLibrary;
  const images = useMemo(() => {
    // Combine extracted frames with library images, avoiding duplicates
    const libraryImageIds = new Set(libraryImages.map(img => img.id));
    const uniqueExtractedFrames = extractedFrames.filter(
      frame => !libraryImageIds.has(frame.id)
    );
    return [...uniqueExtractedFrames, ...libraryImages];
  }, [extractedFrames, libraryImages]);

  // For reels, only use longVideos (reels), not shortVideos (stories)
  const allVideos = longVideos;

  const handleClose = () => {
    onClose();
    reelCreator.resetForm();
    setCurrentStep(editingPost ? "thumbnail" : "video");
    setExtractedFrames([]); // Clear extracted frames on close
  };

  const handleSave = async () => {
    const success = await reelCreator.saveReel();
    if (success) {
      handleClose();
    }
  };

  const handleVideoSelect = (video: MediaItem) => {
    reelCreator.setSelectedVideo(video);
    setCurrentStep("thumbnail");
  };

  const handleBackToVideoSelection = () => {
    reelCreator.setSelectedVideo(null);
    reelCreator.setSelectedThumbnail(null);
    setCurrentStep("video");
  };

  const handleImageThumbnailSelect = (image: MediaItem) => {
    reelCreator.setSelectedThumbnail(image);
  };

  const handleFrameExtracted = async (newMediaItem: MediaItem) => {
    console.log("🎬 Frame extracted, adding to library:", newMediaItem);

    // Add to React Query cache
    if (clientId) {
      mediaCacheUtils.upsertMediaItem(clientId, newMediaItem);
    }

    // Add to local extracted frames list so it appears immediately in the grid
    setExtractedFrames(prev => [newMediaItem, ...prev]);

    // Auto-select the extracted frame as thumbnail immediately
    reelCreator.setSelectedThumbnail(newMediaItem);

    // Refresh media library in background to ensure consistency
    mediaLibrary.refresh();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingPost ? "Edit Reel" : "Create New Reel"}
      size="xl"
      className="max-w-7xl h-[90vh] w-full"
      showCloseButton={false}
    >
      <div className="flex h-full">
        {/* Left Side - Video Preview (fixed width to prevent flashing) */}
        <div
          className={`bg-black flex items-center justify-center transition-all duration-300 ${
            reelCreator.selectedVideo ? "w-2/5" : "w-0 overflow-hidden"
          }`}
        >
          {reelCreator.selectedVideo && (
            <div className="relative h-full w-full flex items-center justify-center">
              <video
                ref={videoRef}
                className="h-full w-full object-contain"
                controls
                playsInline
                preload="auto"
                style={{
                  opacity: videoPlayer.showSpinner || videoPlayer.hasError ? 0 : 1,
                  transition: "opacity 0.2s ease-in-out",
                }}
              />
              {videoPlayer.showSpinner && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-700 border-t-white mx-auto"></div>
                    <p className="text-white mt-3 text-sm">{videoPlayer.userMessage || "Loading video..."}</p>
                  </div>
                </div>
              )}
              {videoPlayer.hasError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-4">
                    <p className="text-white text-sm mb-3">{videoPlayer.userMessage || "Failed to load video"}</p>
                    {videoPlayer.canRetry && (
                      <button
                        onClick={videoPlayer.retry}
                        className="px-4 py-2 bg-white text-black rounded-lg text-sm hover:bg-gray-200"
                      >
                        Try Again
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Controls (takes remaining space) */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingPost ? "Edit Reel" : "Create New Reel"}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {editingPost
                ? "Update video and thumbnail"
                : "Select a video and create or choose a thumbnail"}
            </p>

            {/* Progress indicators */}
            <div className="flex items-center gap-4 mt-4">
              <div
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                  reelCreator.selectedVideo
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    reelCreator.selectedVideo ? "bg-green-500" : "bg-gray-400"
                  }`}
                ></div>
                Video{" "}
                {reelCreator.selectedVideo
                  ? "Selected"
                  : editingPost
                    ? "Current"
                    : "Required"}
              </div>

              <div
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                  reelCreator.selectedThumbnail
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    reelCreator.selectedThumbnail
                      ? "bg-green-500"
                      : "bg-gray-400"
                  }`}
                ></div>
                Thumbnail{" "}
                {reelCreator.selectedThumbnail
                  ? "Selected"
                  : editingPost
                    ? "Current"
                    : "Required"}
              </div>
            </div>

            {/* Show used media toggle */}
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showUsedMedia}
                  onChange={(e) => setShowUsedMedia(e.target.checked)}
                  className="rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Include media already used in posts</span>
              </label>
            </div>

            {/* Error display */}
            {reelCreator.error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-red-700 text-sm font-medium">
                  {reelCreator.error}
                </div>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            {currentStep === "video" ? (
              /* Video Selection Step */
              <div className="h-full p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Select a Video
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose from your video library to create a reel
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto h-full pb-20">
                  {allVideos.map((video) => (
                    <div
                      key={video.id}
                      className="cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-blue-300 hover:ring-offset-2 rounded-lg"
                      onClick={() => handleVideoSelect(video)}
                    >
                      <PostSelectorMediaCard
                        mediaItem={video}
                        clientId={clientId || ""}
                        onClick={() => handleVideoSelect(video)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Thumbnail Creation Step */
              <div className="h-full flex flex-col">
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Choose Thumbnail
                      </h3>
                      <p className="text-sm text-gray-600">
                        Capture a frame from your video or select an existing
                        image
                      </p>
                    </div>
                    <button
                      onClick={handleBackToVideoSelection}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      ← Back to Videos
                    </button>
                  </div>

                  {/* Video Frame Extractor */}
                  {reelCreator.selectedVideo && clientId && (
                    <VideoFrameExtractor
                      videoRef={videoRef}
                      videoItem={reelCreator.selectedVideo}
                      clientId={clientId}
                      onFrameExtracted={handleFrameExtracted}
                      onError={(error) => console.error("Frame extraction error:", error)}
                    />
                  )}

                  {/* Selected Thumbnail Preview */}
                  {reelCreator.selectedThumbnail && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <UnifiedAuthenticatedImage
                          mediaItem={reelCreator.selectedThumbnail}
                          alt="Selected thumbnail"
                          className="w-16 h-20 object-cover rounded-sm border"
                          useThumbnail={true}
                        />
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            ✓ Thumbnail Selected
                          </p>
                          <p className="text-xs text-green-600">
                            {reelCreator.selectedThumbnail.originalFilename ||
                              "Video frame capture"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Or Choose from Image Library
                  </h4>
                </div>

                {/* Available Images Grid */}
                <div className="flex-1 overflow-y-auto p-6 pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((image) => (
                      <div
                        key={image.id}
                        className={`relative cursor-pointer transition-all duration-200 rounded-lg ${
                          reelCreator.selectedThumbnail?.id === image.id
                            ? "ring-4 ring-blue-500 ring-offset-2 shadow-lg"
                            : "hover:ring-2 hover:ring-gray-300 hover:ring-offset-2"
                        }`}
                        onClick={() => handleImageThumbnailSelect(image)}
                      >
                        <PostSelectorMediaCard
                          mediaItem={image}
                          clientId={clientId || ""}
                          isSelected={reelCreator.selectedThumbnail?.id === image.id}
                          onClick={() => handleImageThumbnailSelect(image)}
                        />
                        {/* Selected thumbnail overlay */}
                        {reelCreator.selectedThumbnail?.id === image.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-gray-50 flex justify-between">
            <button
              onClick={handleClose}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>

            <div className="flex space-x-3">
              {currentStep === "thumbnail" && (
                <button
                  onClick={handleBackToVideoSelection}
                  disabled={reelCreator.isCreating}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
                >
                  ← Change Video
                </button>
              )}

              {currentStep === "thumbnail" && (
                <button
                  onClick={handleSave}
                  disabled={!reelCreator.isValid || reelCreator.isCreating}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {reelCreator.isCreating ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {editingPost ? "Updating Reel..." : "Creating Reel..."}
                    </>
                  ) : (
                    `${editingPost ? "Update" : "Create"} Reel${reelCreator.selectedThumbnail ? " ✓" : ""}`
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
